import {Color} from '@benchling/nucleus-ui';
import type {CSSObject, MantineTheme} from '@mantine/core';

import {
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_PADDING_PX,
  REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
  REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
  REDESIGN_HEADER_ICON_COLOR,
} from '../../ui/AIStyleConstants';

/**
 * The redesigned header's icon-button chip: a borderless glyph in a square with a rounded hover
 * background. Shared by the sidebar toggle (hamburger), new chat, chat settings, and the panel
 * expand/collapse controls.
 *
 * The button box is a fixed square so a wide glyph can't turn the hover into a rectangle. The glyph
 * keeps ActionIcon's own size. Locking it to a smaller square letterboxes wide icons such as
 * folder-tree, so they draw smaller than the square icons beside them.
 *
 * Spread this into a `createStyles` rule. The doubled `&&` beats `ActionIcon`'s own box sizing.
 */
export const redesignHeaderIconChipStyles: CSSObject = {
  '&&': {
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    height: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    // The wide header's button group is a zero-width flex container so the title stays centered.
    // Floor the chip at its own size, or that row shrinks the hover down to the padding and it
    // renders as a pill beside the square controls.
    flexShrink: 0,
    minWidth: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    minHeight: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    // ActionIcon's transparent border would offset the glyph relative to the share button's box.
    border: 0,
    padding: REDESIGN_HEADER_ICON_CHIP_PADDING_PX,
    borderRadius: REDESIGN_HEADER_ICON_CHIP_RADIUS_PX,
    color: REDESIGN_HEADER_ICON_COLOR,
  },
  '&&:hover': {
    backgroundColor: REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  },
};

/**
 * Hover treatment for a header title that opens a menu. Padding stays put so the label doesn't shift
 * when the pointer arrives. The text softens on hover; the button itself has no fill.
 */
export function redesignHeaderTitleButtonStyles(theme: MantineTheme): CSSObject {
  return {
    display: 'inline-flex',
    maxWidth: '100%',
    minWidth: 0,
    borderRadius: theme.radius.mdplus,
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    '&:hover, &[aria-expanded="true"]': {
      backgroundColor: 'transparent',
      color: theme.fn.rgba(Color.GRAY_900, 0.64),
    },
  };
}

/**
 * Centers a title menu on its button. The menu is wider than the title, and the dropdown otherwise
 * anchors to the button's left edge. Apply this class to the AbsoluteDropdownContainer.
 */
export const redesignHeaderTitleMenuAnchorStyles: CSSObject = {
  '& > .menu': {
    left: '50%',
    transform: 'translateX(-50%)',
  },
};
